/**
 * Solar Generation Chart
 * Visualizes solar power output over time with area fill
 */

import { AreaChart } from './AreaChart';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useState, useEffect } from 'react';
import { subHours, subDays } from 'date-fns';

interface SolarGenerationChartProps {
  siteId: string;
}

export function SolarGenerationChart({ siteId }: SolarGenerationChartProps) {
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');

  const getTimeRange = () => {
    const end = new Date();
    const start = timeRange === '24h'
      ? subHours(end, 24)
      : timeRange === '7d'
      ? subDays(end, 7)
      : subDays(end, 30);

    return { start, end };
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['solar-generation', siteId, timeRange],
    queryFn: async () => {
      const { start, end } = getTimeRange();
      const interval = timeRange === '24h' ? '5min' : '1hour';

      const response = await api.get(
        `/v1/sites/${siteId}/timeseries?` +
        `asset_type=solar_pv&` +
        `kpis=active_power&` +
        `start=${start.toISOString()}&` +
        `end=${end.toISOString()}&` +
        `interval=${interval}`
      );

      return response.data.data.series.active_power || [];
    },
    refetchInterval: 30000,
    enabled: !!siteId
  });

  useEffect(() => {
    if (isError) {
      console.error('SolarGenerationChart: timeseries query failed', error);
    }
  }, [isError, error]);

  return (
    <div className="rounded-xl border border-slate-700/50 p-6 backdrop-blur-md"
      style={{
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(51, 65, 85, 0.5) 100%)'
      }}>

      {/* Header with time range selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Solar Power Generation
        </h3>

        <div className="flex gap-2">
          {(['24h', '7d', '30d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded-lg text-sm transition-all ${
                timeRange === range
                  ? 'bg-djin-gold text-djin-navy'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {isError && (!data || data.length === 0) ? (
        <div
          className="bg-slate-800/30 rounded-lg flex items-center justify-center"
          style={{ height: 300 }}
          role="alert"
        >
          <p className="text-slate-400">Unable to load chart data</p>
        </div>
      ) : (
        <AreaChart
          data={data}
          series={[{
            name: 'Active Power',
            dataKey: 'value',
            color: '#FFD700',
            unit: 'kW',
            strokeWidth: 2,
            fillOpacity: 0.3
          }]}
          height={300}
          yAxisDomain={['auto', 'auto']}
          showBrush={false}
          loading={isLoading}
        />
      )}

      {/* Legend */}
      <div className="flex gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <span className="text-slate-300">Solar Generation (kW)</span>
        </div>
      </div>
    </div>
  );
}
